import p2 from 'p2';
import { Container, Sprite, utils } from 'pixi.js';

import { Shared } from '../../App';
import { DisplayUtils } from '../../utils/DisplayUtils';
import { BlinkComponent } from '../components/BlinkComponent';
import { GHOST_COLOR, GHOST_COLOR_MIX } from '../Game';
import { PhysicsCollisionGroup } from '../physics';
import { PhysicsComponent } from '../physics/PhysicsComponent';
import { PhysicsShapeData, PhysicsUtils } from '../physics/PhysicsUtils';
import { ReplayBodyComponent } from '../replay';
import { BaseEntity } from './BaseEntity';
import { WindmillBladesEntity } from './WindmillEntity';

export interface WheelData
{
    id: string,
    tier: number,
    type: string,
    mass: number,
    sprites: {id: string, rotate: boolean | number, tint?: string}[],
    shapes: PhysicsShapeData[],
    ghost?: boolean
    available?: boolean,
    lockRotation?: boolean,
    coinPrice?: number,
    diamondPrice?: number,
    stats: {
        speed: number,
        flipSpeed: number,
        handling: number,
        bounce: number
    }
    unlocked?: boolean;
}

export class WheelEntity extends BaseEntity<WheelData>
{
    public physics: PhysicsComponent;
    public replay: ReplayBodyComponent;
    public blink: BlinkComponent;
    public viewHeight = 1;
    public contacting = false;

    private contactingChanged = 0;

    constructor()
    {
        super();
        this.physics = this.addComponent(PhysicsComponent);
        this.replay = this.addComponent(ReplayBodyComponent);
        this.blink = this.addComponent(BlinkComponent);
    }

    public build(data: WheelData): void
    {
        this.data = data;
        this.view.removeChildren();
        this.physics.clearParts();
        WheelEntity.mountSprite(this.view, data);
        this.physics.viewsToRotate = [];
        this.physics.useRotationRate = false;
        this.viewHeight = this.view.height;

        for (const sprite of this.view.children)
        {
            if (sprite.rotation <= 0) continue;
            if (!this.physics.viewsToRotate) this.physics.viewsToRotate = [];
            this.physics.viewsToRotate.push(sprite as Sprite);
            if ((sprite as any).rotationRate !== undefined) this.physics.useRotationRate = true;
        }

        const body = PhysicsUtils.createBody({
            mass: data.ghost ? 0 : data.mass,
        }, ...data.shapes);

        const material = data.ghost ? undefined : Shared.game.physics.contact.getMaterial({
            // name: `wheel-${data.id}`,
            name: `wheel`,
            friction: 2,
            // restitution: (data.stats.bounce + 2) / 4 * 0.1,
            // stiffness: Number.MAX_VALUE,
        });

        PhysicsUtils.setupShapes(body.shapes, {
            group: data.ghost ? 0 : PhysicsCollisionGroup.VEHICLE,
            mask: data.ghost ? 0 : PhysicsCollisionGroup.LEVEL,
            material: data.ghost ? undefined : material,
            sensor: data.ghost,
        });

        this.physics.addParts(body);
    }

    public updateColor(color: string): void
    {
        for (let i = 0; i < this.view.children.length; i++)
        {
            const spr = this.view.children[i] as Sprite;
            const sprData = this.data?.sprites[i];

            if (!sprData || !sprData.tint)
            {
                spr.tint = this.data?.ghost && GHOST_COLOR_MIX ? GHOST_COLOR : 0xFFFFFF;
                continue;
            }

            const tint = utils.string2hex(color);
            const mixed = DisplayUtils.mixColors(tint, GHOST_COLOR, GHOST_COLOR_MIX);

            spr.tint = this.data?.ghost ? mixed : tint;
        }
    }

    public static mountSprite(container: Container, data: WheelData, color = '#FFCC00'): Container
    {
        for (const spriteData of data.sprites)
        {
            const sprite = Sprite.from(Shared.assets.getTexture(spriteData.id));

            sprite.anchor.set(0.5);
            sprite.tint = spriteData.tint ? utils.string2hex(color) : 0xFFFFFF;
            if (spriteData.rotate)
            {
                sprite.rotation = 0.00001;
                if (typeof spriteData.rotate === 'number')
                {
                    // Inject rotation ratio, if defined - to figure out a better way of doing it
                    (sprite as any).rotationRate = spriteData.rotate;
                }
            }
            container.addChild(sprite);
        }

        return container;
    }

    protected onBeginContact(other: p2.Body): void
    {
        this.setContacting(true);

        if (this.game && other.physicsComponent?.ground)
        {
            this.game.vehicle.trick.trickReset();
            
        }
        
        if(this.game){
            let aX = Math.cos(this.game.vehicle.chassis.physics.mainBody.angle);
            let aY = Math.sin(this.game.vehicle.chassis.physics.mainBody.angle);
            let dotProduct = (aX * 1) + (aY * 0);
            //1 = completely upright, -1 completely upside down
            if(dotProduct < 0 && other.physicsComponent?.entity instanceof WindmillBladesEntity){
                //Prevents windmills from being able to shoot us underground
                this.game.vehicle.crash();
            }
        }
        
    }

    protected onEndContact(other: p2.Body): void
    {
        this.setContacting(false);

        if (this.game && other.physicsComponent?.ground)
        {
            this.game.vehicle.trick.trickReset();
        }
    }

    protected setContacting(val: boolean): void
    {
        if (val !== this.contacting) this.contactingChanged += 1;

        if (this.contactingChanged > 2)
        {
            this.contacting = val;
            this.contactingChanged = 0;
        }
    }
}
